import { Menu, Phone, Mail, Instagram, MessageCircle } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { useMemo } from 'react';

import type { SiteContent } from '../../content/types';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogTrigger } from '../ui/dialog';
import { Container } from './container';

interface SiteHeaderProps {
  content: SiteContent;
}

export function SiteHeader({ content }: SiteHeaderProps): JSX.Element {
  const navItems = content.navigation.primary;
  const sortedNav = useMemo(() => navItems, [navItems]);

  return (
    <header className="sticky top-0 z-40 border-b border-[color:var(--color-border)] bg-white/95 backdrop-blur-md">
      <div className="border-b border-[color:var(--color-border)] bg-[color:var(--color-surface-alt)]/50">
        <Container className="flex min-h-11 flex-wrap items-center justify-between gap-3 py-2 text-xs text-[color:var(--color-muted)] sm:text-sm">
          <div className="flex items-center gap-4">
            <a href={content.topbar.phoneHref} className="inline-flex items-center gap-1.5 hover:text-[color:var(--color-primary)]">
              <Phone className="h-3.5 w-3.5" />
              <span>{content.topbar.phoneLabel}</span>
              <span>{content.topbar.phoneDisplay}</span>
            </a>
            <a href={content.topbar.emailHref} className="hidden items-center gap-1.5 hover:text-[color:var(--color-primary)] sm:inline-flex">
              <Mail className="h-3.5 w-3.5" />
              <span>{content.topbar.emailDisplay}</span>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden lg:inline">{content.topbar.openingHoursCompact}</span>
            <a href={content.topbar.instagramUrl} className="inline-flex items-center gap-1.5 hover:text-[color:var(--color-primary)]" target="_blank" rel="noreferrer">
              <Instagram className="h-3.5 w-3.5" />
              <span>{content.topbar.instagramLabel}</span>
            </a>
            <a href={content.topbar.whatsappHref} className="inline-flex items-center gap-1.5 hover:text-[color:var(--color-primary)]" target="_blank" rel="noreferrer">
              <MessageCircle className="h-3.5 w-3.5" />
              <span>{content.topbar.whatsappLabel}</span>
              <span>{content.topbar.whatsappDisplay}</span>
            </a>
          </div>
        </Container>
      </div>

      <Container className="flex min-h-20 items-center justify-between gap-4">
        <NavLink to="/" className="inline-flex items-center gap-3">
          {content.brand.hasLogoAsset ? (
            <img src={content.brand.logoPath} alt={content.brand.name} className="h-10 w-auto rounded-lg" />
          ) : (
            <span className="text-lg font-semibold text-[color:var(--color-text)]">{content.brand.shortName}</span>
          )}
          <div className="hidden lg:block">
            <p className="text-sm font-semibold text-[color:var(--color-text)]">{content.brand.name}</p>
            <p className="text-xs text-[color:var(--color-muted)]">{content.brand.locationLabel}</p>
          </div>
        </NavLink>

        <nav aria-label={content.brand.name} className="hidden items-center gap-1 lg:flex">
          {sortedNav.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              className={({ isActive }) =>
                cn(
                  'rounded-xl px-4 py-2 text-sm font-semibold transition-colors',
                  isActive ? 'bg-[color:var(--color-primary-soft)] text-[color:var(--color-primary)]' : 'text-[color:var(--color-muted)] hover:text-[color:var(--color-text)]',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          <Button asChild variant="default">
            <a href={content.navigation.mobileCta.href}>{content.navigation.mobileCta.label}</a>
          </Button>
        </div>

        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" size="icon" className="lg:hidden" aria-label={content.brand.shortName}>
              <Menu className="h-5 w-5" />
            </Button>
          </DialogTrigger>
          <DialogContent className="left-auto right-4 top-24 w-[calc(100%-2rem)] max-w-sm translate-x-0 translate-y-0 rounded-2xl">
            <div className="space-y-4">
              {sortedNav.map((item) => (
                <NavLink
                  key={item.href}
                  to={item.href}
                  className={({ isActive }) =>
                    cn(
                      'block rounded-xl px-4 py-3 text-sm font-semibold transition-colors',
                      isActive ? 'bg-[color:var(--color-primary-soft)] text-[color:var(--color-primary)]' : 'text-[color:var(--color-text)] hover:bg-[color:var(--color-surface-alt)]',
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <Button asChild className="w-full">
                <a href={content.navigation.mobileCta.href}>{content.navigation.mobileCta.label}</a>
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </Container>
    </header>
  );
}
